/**
 * `localAnswer()` — the deterministic local engine behind the OEM+ assistant.
 *
 * A thin facade (fork, oem v1.8). The engine it fronts is in this folder:
 *
 * | Piece | File |
 * |---|---|
 * | Entities a question names (machines, mines by case-sensitive code or name, classes, components, ids, A$, windows, deixis) | `./entities.ts` |
 * | What the route is about (machine, mine, alert, incident, module, report) | `./page-context.ts` |
 * | The scored classifier over the intent registry | `./router.ts` |
 * | The intents, one file per family, and the registry | `./intents/` |
 * | Follow-ups and the per-thread memory | `./conversation.ts` |
 * | The persona lead line | `./persona-lens.ts` |
 * | "Here's what I can answer" | `./fallback.ts` |
 * | The tools the intents call | `./tools.ts` |
 *
 * It replaced a port of the demo's `localAnswer(q, ph, append)` (demo line
 * 4053): twenty-four `if` branches tested in source order, first match wins.
 * Every branch survives as an intent with the same answer for the questions
 * it routed correctly; the branch comments carry their `demo L<n>` refs. What
 * changed is routing (scored, with entities, page context and memory), the
 * new intents, and the fallback. No LLM, by decision — same question, page,
 * persona and memory in, same answer out; no clock, no randomness.
 *
 * ## The answer contract
 *
 * {@link OemAnswer} — `{ markdown, sources, tools, navigate?, artifact?,
 * report?, actions?, memory? }` — defined in `./answer-types.ts` and
 * re-exported here with the other names callers import.
 *
 * - `tools` is the call trace, rendered as the "Ran a → b" line, ending with
 *   `(local engine)`. `onProgress` is called with it as it grows.
 * - `memory` is what the next turn of the same thread should pass back.
 *
 * Engine layer: async because the data is lazy, otherwise pure.
 *
 * @see docs/logic/workspace/chat-rail.md — the OEM+ local engine section
 * @see scripts/oem/check-assistant.mjs — the regression gate
 */

import type { MineCode } from '../../schema/machine.ts';
import { newAnswerKit } from './answer-kit.ts';
import type { LocalAnswerOptions, OemAnswer } from './answer-types.ts';
import { nextMemory } from './conversation.ts';
import { fallbackAnswer } from './fallback.ts';
import { personaLens } from './persona-lens.ts';
import { classify } from './router.ts';
import { REPORT_TITLE, type ReportKind } from './tools.ts';

export type {
  ConversationMemory,
  LocalAnswerOptions,
  OemAnswer,
  OemAnswerAction,
  OemAnswerArtifact,
  OemAnswerReport,
} from './answer-types.ts';
export { BRIDGING } from './bridging.ts';
export { REPORT_CHOICES } from './intents/reports.ts';
export { classify } from './router.ts';

// ===========================================================================
// Legacy helpers — kept exported for callers of the regex engine's API
// ===========================================================================

/**
 * Demo line 2678. `mineFromText` — the site a lowercased question names.
 *
 * Legacy: it cannot tell "Oh," from Ochre Hill because it sees lowercase
 * text. The engine uses `minesFromText()` in `./entities.ts`, which matches
 * codes only in capitals.
 */
export function mineFromText(L: string): MineCode | null {
  if (/kalbarri|\bkg\b/.test(L)) return 'KG';
  if (/broken ridge|\bbr\b|cobar/.test(L)) return 'BR';
  if (/isolde|\bmi\b|redgum/.test(L)) return 'MI';
  if (/ochre|\boh\b|barkly/.test(L)) return 'OH';
  if (/gawler|\bgd\b|spinifex/.test(L)) return 'GD';
  if (/mount read|mt read|\bmr\b|tasmania|\btas\b|western tiers/.test(L)) return 'MR';
  return null;
}

/** What {@link reportIntent} decided. `'ask'` means "which of the six?". */
export type ReportIntent = { kind: ReportKind | 'ask'; mine?: MineCode | null };

/**
 * Demo line 2679. `reportIntent` — does this question want a document?
 *
 * Legacy: the router uses `reportRequest()` in `./intents/reports.ts`, which
 * needs a generate verb and a document noun and leaves "this report" alone.
 */
export function reportIntent(L: string): ReportIntent | null {
  const mine = mineFromText(L);
  if (/s\.?o\.?s\b|hotspot|daily report|morning report|service operations summary/.test(L)) {
    return { kind: 'sos', mine };
  }
  if (
    /\b(e-?mail)\b/.test(L) &&
    /risk|summary|week|status|technical|team|site|write|draft|send|generate|create|make|build|prepare|do\b/.test(L)
  ) {
    return { kind: 'email', mine: mine ?? 'MI' };
  }
  if (/\b(deck|presentation|slides|slide deck|powerpoint|pptx)\b/.test(L)) {
    return { kind: 'deck', mine: mine ?? 'BR' };
  }
  const wantsReport =
    /\b(weekly|status report|weekly status|report)\b/.test(L) &&
    /generate|create|make|build|write|prepare|produce|give me|run|can you|please|report|status|need|want/.test(L);
  if (!wantsReport) return null;
  if (/surface|blasthole|top-?hammer|open pit|dth/.test(L)) return { kind: 'surface' };
  if (/drill|jumbo|production rig|longhole|development/.test(L)) return { kind: 'drills' };
  if (/load|haul|loader|truck|lhd/.test(L)) return { kind: 'loadhaul' };
  if (/risk/.test(L) && mine) return { kind: 'email', mine };
  return { kind: 'ask' };
}

/**
 * Demo line 4041. `splitSources` — peel the trailing `SOURCES: a · b · c` line
 * off a model reply. Unused by the local engine, which carries sources as
 * data. Kept for the Tier 3 live mode, whose replies arrive as one string.
 */
export function splitSources(text: string): { body: string; srcs: string[] } {
  const m = /SOURCES?:\s*(.+)$/im.exec(text);
  if (!m) return { body: text, srcs: [] };
  return {
    body: text.replace(m[0], '').trim(),
    srcs: m[1]
      .split(/\s*[·;,]\s*/)
      .map((x) => x.trim())
      .filter(Boolean)
      .slice(0, 6),
  };
}

// ===========================================================================
// localAnswer
// ===========================================================================

/**
 * Answer one question from the local engine.
 *
 * @param question raw user text
 * @param options persona, route, the thread's memory and a progress callback
 */
export async function localAnswer(
  question: string,
  options: LocalAnswerOptions,
): Promise<OemAnswer> {
  const q = question.trim();
  const kit = newAnswerKit(options.personaId, options.pathname, options.onProgress);
  const c = classify(q, { pathname: options.pathname, memory: options.memory });

  if (!c.intent) {
    const answer = fallbackAnswer(q, c.runnersUp, c.page, kit);
    return {
      ...answer,
      memory: nextMemory({ intent: null, q, E: c.entities, focus: {}, memo: {}, previous: options.memory }),
    };
  }

  let answer: OemAnswer;
  try {
    answer = await c.intent.answer({
      q: c.q,
      L: c.q.toLowerCase(),
      page: c.page,
      memory: options.memory,
      E: c.entities,
      personaId: options.personaId,
      pathname: options.pathname,
      kit,
    });
  } catch (e) {
    // An intent that cannot answer (an unknown id, a missing record) says so
    // and offers the nearest questions, rather than failing the turn.
    const fb = fallbackAnswer(q, c.runnersUp, c.page, kit);
    return {
      ...fb,
      markdown: `I matched that to **${c.intent.examples[0]}** but couldn't answer it: ${
        e instanceof Error ? e.message : String(e)
      }\n\n${fb.markdown}`,
      memory: options.memory,
    };
  }
  const lens = await personaLens(options.personaId, c.intent.id, kit.focused);
  return {
    ...answer,
    markdown: lens ? `${lens}\n\n${answer.markdown}` : answer.markdown,
    memory: nextMemory({
      intent: c.intent.id,
      q: c.q,
      E: c.entities,
      focus: kit.focused,
      memo: kit.memo,
      family: c.intent.family,
      previous: options.memory,
      followUp: c.followUp,
    }),
  };
}

/** Re-exported so a caller can label a report card without importing `tools.ts`. */
export { REPORT_TITLE };
